<template>
    <div class="mb-5">

        <!-- Create Entry Form -->
        <b-skeleton-wrapper
            :loading="!currentState.matches('loaded')">
            <!-- Loading State -->
            <template #loading>
                <div class="CreateEntryFormContainer py-3 py-md-5">
                    <b-container>
                        <b-row class="justify-content-center">
                            <b-col md="8">
                                <b-row>
                                    <div class="input-group col-12 col-sm pr-sm-0 mr-sm-2 mb-2 mb-md-0">
                                        <b-skeleton width="100%" type="input"></b-skeleton>
                                    </div>
                                    <div class="col-12 col-sm-auto pl-sm-0">
                                        <b-skeleton
                                            v-if="$mq === 'xs' || $mq === 'sm'"
                                            width="100%" type="button"></b-skeleton>
                                        <b-skeleton
                                            v-if="!($mq === 'xs' || $mq === 'sm')"
                                            type="button"></b-skeleton>
                                    </div>
                                </b-row>
                            </b-col>
                        </b-row>
                    </b-container>
                </div>
            </template>

            <!-- Loaded State -->
            <div class="CreateEntryFormContainer py-3 py-md-5">
                <b-container>
                    <b-row class="justify-content-center">
                        <b-col md="8">
                            <create-entry-form
                                :execCreateEntryRequest="execCreateEntryRequest"
                                :disabled="currentState.matches('failure')
                                    || currentState.matches('loading')"
                                @entryCreated="onEntryCreated"
                            />
                        </b-col>
                    </b-row>
                </b-container>
            </div>
        </b-skeleton-wrapper>

        <div class="border-bottom border-top position-relative">
            <b-container>
                <div class="BreadcrumbsContainer">
                    <div class="BreadcrumbsContainer-breadcrumbs">
                        <b-breadcrumb
                            v-if="currentState.matches('loading')"
                            class="pl-0 mb-0">
                            <b-breadcrumb-item active>My List</b-breadcrumb-item>
                            <b-breadcrumb-item active>...</b-breadcrumb-item>
                            <b-breadcrumb-item active>...</b-breadcrumb-item>
                        </b-breadcrumb>

                        <b-breadcrumb
                            v-else
                            class="pl-0 mb-0">
                            <b-breadcrumb-item
                                v-for="(breadcrumb, index) in breadcrumbs"
                                :key="`breadcrumb-${breadcrumb.uuid}`"
                                :to="breadcrumb.to"
                                :active="breadcrumb.active"
                                @dragover.prevent
                                @drop.prevent="onBreadcrumbDrop($event, { target: breadcrumb.uuid })">
                                <b-dropdown
                                    variant="outline-primary"
                                    :text="breadcrumb.text"
                                    size="sm"
                                    v-if="index === breadcrumbs.length - 1 && currentFolder.is_root === false"
                                    class="breadcrumb-dropdown"
                                >
                                    <b-dropdown-item @click="onRenameItemClicked">Rename</b-dropdown-item>
                                </b-dropdown>
                                <span
                                    v-else
                                >
                            {{ breadcrumb.text }}
                        </span>
                            </b-breadcrumb-item>

                        </b-breadcrumb>
                    </div>

                    <mq-layout :mq="['lg', 'xl']" class="BreadcrumbsContainer-hotkeys">
                        <div class="HotkeysInfoLabel">
                            <div class="HotkeysInfoLabel-inner">
                                <button class="HotkeysInfoLabel-button" @click="onHotkeysLabelClick">
                                    <fa-icon icon="info-circle" class="HotkeysInfoLabel-icon" />
                                    <span>Hotkeys (ctrl/cmd + k)</span>
                                </button>
                            </div>
                        </div>
                    </mq-layout>
                </div>
            </b-container>
        </div>

        <div>
            <div class="mt-4">
                <b-container v-if="context.errors.length">
                    <div class="row justify-content-center">
                        <div class="col-md-8">
                            <b-alert
                                v-for="(error, index) in context.errors"
                                :key="`error-${index}`"
                                :show="true"
                                variant="danger">
                                {{ error.text }}
                            </b-alert>
                        </div>
                    </div>
                </b-container>

                <div v-if="currentState.matches('failure')" class="d-flex justify-content-center pt-3">
                    <b-button
                        v-if="currentState.matches('failure.idle')"
                        variant="primary"
                        class="d-md-block"
                        @click="onRetryButtonClick"
                    >
                        Retry Now
                    </b-button>
                    <b-button
                        v-if="currentState.matches('failure.retrying')"
                        variant="primary"
                        class="d-md-block"
                        disabled
                    >
                        <span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
                        Retrying...
                    </b-button>
                </div>

                <b-skeleton-wrapper
                    :loading="!currentState.matches('loaded')">

                    <!-- Loading State -->
                    <template #loading>
                        <b-container class="pt-3">
                            <div class="mb-3">
                                <h4>Folders</h4>
                            </div>
                            <!-- start folder list -->
                            <b-row>
                                <b-col v-for="i in 8"
                                       cols="12" sm="6" lg="3"
                                       class="mb-3"
                                       :key="`folder-skeleton-${i}`">
                                    <b-skeleton height="57px" width="100%"></b-skeleton>
                                </b-col>
                            </b-row>
                        </b-container>

                        <b-container class="pt-3">
                            <!-- Entries -->
                            <h4 class="mt-4">Entries</h4>

                            <!-- start entries list -->
                            <b-skeleton
                                v-for="i in 8"
                                :key="`entry-skeleton-${i}`"
                                height="43px"
                                width="100%"
                                class="mb-2"
                            />
                        </b-container>
                    </template>

                    <!-- Loaded State -->
                    <b-container class="pt-3">
                        <div class="mb-3">
                            <h4>Folders</h4>
                        </div>

                        <div v-if="folders.length === 0">
                            <p class="color-undertone">Folders can help you organize your saved content by source,
                                project, topic, or really anything you can think of!</p>
                        </div>
                        <!-- start folder list -->
                        <folder-list
                            @newFolderButtonClicked="onNewFolderButtonClick"
                            @folderDroppedOn="onFolderDroppedOn"
                            :folders="folders"
                        />
                    </b-container>

                    <!-- start entries list -->
                    <b-container class="pt-3">

                        <!-- Entries -->
                        <h4 class="mt-4">Entries</h4>

                        <entry-list-item
                            v-for="entry in entries"
                            :key="entry.data.id"
                            :entry="entry.data"
                            :entryRef="entry.ref"
                        />

                        <div v-if="currentState.matches('loaded') && context.entries.length === 0"
                             class="pt-2">
                            <p class="color-undertone mb-1">Entries are the URLs that link to the awesome content you've
                                found around the web.</p>
                            <p class="color-undertone">Get started by entering a URL into the "Save a URL" field above,
                                and then click the "Save" button.</p>
                        </div>

                        <!-- Archived Entries -->
                        <div v-if="archivedEntries.length !== 0">
                            <h4 class="mt-4">Archived Entries</h4>

                            <entry-list-item
                                v-for="entry in archivedEntries"
                                :key="entry.data.id"
                                :entry="entry.data"
                                :entryRef="entry.ref"
                            />
                        </div>
                    </b-container>
                </b-skeleton-wrapper>
            </div>
        </div>

        <!-- Create Folder Modal -->
        <b-modal
            id="create-folder-modal"
            title="New Folder"
            :visible="currentState.matches('loaded.new_folder_modal_open')"
            @hidden="entryIndexService.send('MODAL_CLOSE')"
            @hide="onCreateFolderModalHide"
            hide-footer
        >
            <create-folder-form
                :submitRequest="execCreateFolderRequest"
                @done="onCreateFolderFormCompleted"
                ref="createFolderFormRef"
            />
        </b-modal>

        <!-- Rename Folder Modal -->
        <b-modal
            id="rename-folder-modal"
            title="Rename Folder"
            :visible="currentState.matches('loaded.rename_folder_modal_open')"
            v-if="currentState.matches('loaded')"
            @hidden="entryIndexService.send('MODAL_CLOSE')"
            @shown="onRenameFolderModalShown"
            @hide="onRenameFolderModalHide"
            hide-footer
        >
            <rename-folder-form
                :submitRequest="execRenameFolderRequest"
                @done="onRenameFolderFormCompleted"
                :currentTitle="currentFolder.title"
                ref="renameFolderFormRef"
            />
        </b-modal>

        <!-- Rename Entry Modal -->
        <b-modal
            id="rename-entry-modal"
            title="Rename"
            :visible="currentState.matches('loaded.rename_entry_modal_open')"
            @hidden="entryIndexService.send('MODAL_CLOSE')"
            @hide="onRenameEntryModalHide"
            hide-footer
        >
            <rename-entry-form
                @done="onRenameEntryFormCompleted"
                ref="renameEntryFormRef"
                :editingEntry="context.editingEntry"
                :submitRequest="execRenameEntryRequest"
                v-if="context.editingEntry"
            />
        </b-modal>

        <!-- QR Code Overlay -->
        <b-overlay
            id="overlay-background"
            v-if="currentState.matches('loaded.qr_overlay_open')"
            :show="currentState.matches('loaded.qr_overlay_open')"
            blur="2px"
            fixed
            no-center
            no-wrap
            opacity="0.87"
            variant="light"
            @click.native="entryIndexService.send('CLOSE_QR_OVERLAY')"
        >
            <template v-slot:overlay>
                <div>
                    <focus-trap
                        v-model="qrCodeOverlayFocusTrapActive"
                        :allowOutsideClick="true"
                        :escapeDeactivates="false">
                        <button
                            aria-label="Close"
                            class="btn btn-default QrCodeOverlay-closeButton"
                            tabindex="0"
                            @click="entryIndexService.send('CLOSE_QR_OVERLAY')">
                            <fa-icon class="color-brand" icon="window-close" size="2x"/>
                        </button>
                    </focus-trap>
                    <qrcode
                        :options="{ width: 200 }"
                        :value="context.qrCodeEntry.data.url"
                        class="QrCodeOverlay-codeCanvas"/>
                </div>
            </template>
        </b-overlay>

        <ninja-keys
            ref="ninjaKeys"
            :placeholder="ninjaKeys.placeholder"
            :hideBreadcrumbs="true"
        ></ninja-keys>
    </div>
</template>

<script>
import {interpret} from 'xstate';
import {createFolderIndexMachine} from './FolderIndexMachine.js';
import FolderList from "./FolderList";
import gql from 'graphql-tag';
import "ninja-keys";
import CreateFolderModal from "./CreateFolderModal";
import CreateFolderForm from "./CreateFolderForm/CreateFolderForm";
import {XSTATE_DEBUG_ENABLED} from "../../../constants.js";
import helpers from "../../../helpers/index.js";
import RenameFolderForm from "./RenameFolderForm/RenameFolderForm";

export default {
    components: {RenameFolderForm, CreateFolderForm, CreateFolderModal, FolderList},

    created() {
        this.entryIndexService
            .onTransition((state) => {
                this.currentState = state;
                this.context = state.context;
            })
            .start();
    },

    mounted() {
        this.$refs.ninjaKeys.data = this.ninjaKeys.hotkeys;
    },

    /*
     * The component's data.
     */
    data() {
        const resultToastActions = {
            'ENTRY.CREATED': (context, event) => {
                this.$toasted.global.appSuccess({ message: 'Entry saved' });
            },
            'ENTRY.ARCHIVED': (context, event) => {
                this.$toasted.global.appSuccess({ message: 'Entry archived' });
            },
            'ENTRY.UNARCHIVED': (context, event) => {
                this.$toasted.global.appSuccess({ message: 'Entry unarchived' });
            },
            'ENTRY.TOGGLE_ARCHIVED_FAILURE': (context, event) => {
                this.$toasted.global.appError({ message: 'Failed to archive entry' });
            },
            'ENTRY.DELETE_SUCCESS': (context, event) => {
                this.$toasted.global.appSuccess({ message: 'Entry deleted' });
            },
            'ENTRY.DELETE_FAILURE': (context, event) => {
                this.$toasted.global.appError({ message: 'Failed to delete entry' });
            },
            'ENTRY.REFRESH_TITLE_SUCCESS': (context, event) => {
                this.$toasted.global.appSuccess({ message: 'Title updated' });
            },
            'ENTRY.REFRESH_TITLE_FAILURE': (context, event) => {
                this.$toasted.global.appError({ message: 'Failed to update title' });
            },
            'ENTRY.UPDATE_META': (context, event) => {
                this.$toasted.global.appSuccess({ message: 'Entry updated' });
            },
            'ENTRY.MOVED': (context, event) => {
                this.$toasted.global.appSuccess({ message: 'Entry moved' });
            },
            'FOLDER.CREATED': (context, event) => {
                this.$toasted.global.appSuccess({ message: 'Folder created' });
            },
            'FOLDER.RENAMED': (context, event) => {
                this.$toasted.global.appSuccess({ message: 'Folder renamed' });
            },
            'FOLDER.MOVED': (context, event) => {
                this.$toasted.global.appSuccess({ message: 'Folder moved' });
            },
        };

        const currentFolderId = this.$route.params.folderId;

        const initialMachineContext = {
            folderUUID: currentFolderId,
            entries: [],
            folders: [],
            errors: []
        };

        const initialMachineConfig = {
            services: {
                fetchCurrentFolder: async ({folderUUID}, event) => {
                    return this.execFetchFolder({folderId: folderUUID});
                }
            }
        };

        const initialMachineOptions = {
            methods: {
                deleteEntry: ({id}) => {
                    return axios.delete(`/me/entries/${id}`);
                },
                toggleArchiveEntry: ({id, value}) => {
                    return axios.put(`/me/entries/${id}`, {
                        is_archived: value
                    });
                },
                refreshEntryTitle: ({id}) => {
                    return this.execRefreshEntryMeta(id);
                }
            },
            actions: {
                addNoScrollBody: () => document.querySelector("body").classList.add("noscroll"),
                removeNoScrollBody: () => document.querySelector("body").classList.remove("noscroll"),
                resultToast: (context, event) => {
                    const action = resultToastActions[event.type];
                    if (action) action(context, action);
                }
            }
        };

        const entryIndexMachine = createFolderIndexMachine(initialMachineOptions)
            .withContext(initialMachineContext)
            .withConfig(initialMachineConfig);

        const currentState = entryIndexMachine.initialState;

        return {
            entryIndexService: interpret(entryIndexMachine, {
                devTools: XSTATE_DEBUG_ENABLED
            }),
            currentState,
            context: entryIndexMachine.context,
            createFolderModalShown: currentState.matches('loaded.new_folder_modal_open'),
            ninjaKeys: {
                placeholder: "Enter command name",
                hotkeys: [
                    {
                        id: "Home",
                        title: "Home",
                        hotkey: "ctrl+h",
                        mdIcon: "home",
                        handler: () => {
                            if (this.context.currentFolder.is_root === false) {
                                this.$toasted.global.appInfo({ message: 'Action: Open Home Folder (ctrl/cmd + h)' });
                                this.$router.push({
                                    path: `/my-list/folders/${this.context.rootFolder.uuid}`
                                });
                            } else {
                                this.$toasted.global.appError({ message: 'Already in home folder (ctrl/cmd + h)' });
                            }
                        },
                    },
                    {
                        id: "Create Folder",
                        title: "Create Folder",
                        hotkey: "ctrl+.",
                        mdIcon: "folder",
                        handler: () => {
                            this.$toasted.global.appInfo({ message: 'Action: Create Folder (ctrl+.)' });
                            this.entryIndexService.send({type: 'SHOW_CREATE_FOLDER_MODAL'});
                        },
                    },
                    {
                        id: "Rename Current Folder",
                        title: "Rename Current Folder",
                        hotkey: "f2",
                        mdIcon: "drive_file_rename_outline",
                        handler: () => {
                            if (this.context.currentFolder.is_root === false) {
                                this.$toasted.global.appInfo({ message: 'Action: Rename Folder (F2)' });
                                this.entryIndexService.send({type: 'SHOW_RENAME_FOLDER_MODAL'});
                            } else {
                                this.$toasted.global.appError({ message: 'Cannot rename home folder' });
                            }
                        },
                    }
                ]
            },
        };
    },

    mixins: [helpers],

    watch: {
        '$route': 'onRouteChange'
    },

    computed: {
        breadcrumbs() {

            if (!this.currentState.matches('loaded')) {
                return [{
                    text: "My List",
                    active: true
                }, {
                    text: "...",
                    active: true
                }, {
                    text: "...",
                    active: true
                }];
            }

            let {parent, currentFolder, rootFolder} = this.context;
            const items = [];
            if (currentFolder.is_root === false) {
                items.push({
                    text: this.truncate(currentFolder.title, 20),
                    uuid: currentFolder.uuid,
                    active: true
                });
            }
            while (parent != null && parent.is_root === false) {
                items.push({
                    text: this.truncate(parent.title, 12),
                    uuid: parent.uuid,
                    to: `/my-list/folders/${parent.uuid}`
                });
                parent = parent.parent;
            }
            items.push({
                text: 'My List',
                to: `/my-list/folders/${rootFolder.uuid}`,
                uuid: rootFolder.uuid,
                active: items.length === 0
            });
            const reversed = items.reverse();
            if (reversed.length === 6) {
                reversed[1] = {
                    text: '...',
                    active: true
                };
            }
            return reversed;
        },
        entries() {
            return this.context.entries.filter((e) => {
                return !e.data.is_archived;
            });
        },
        folders() {
            return this.context.folders;
        },
        archivedEntries() {
            return this.context.entries.filter((e) => {
                return e.data.is_archived;
            });
        },
        qrCodeOverlayFocusTrapActive: {
            get() {
                return this.currentState.matches('loaded.qr_overlay_open');
            },
            set(value) {
                return value;
            }
        },
        currentFolder() {
            return this.context.currentFolder;
        }
    },

    methods: {
        onRouteChange() {
            this.entryIndexService.send({
                type: 'ROUTE_CHANGE',
                folderId: this.$route.params.folderId
            });
        },
        onEntryCreated({entry: newEntry}) {
            this.entryIndexService.send({type: 'ENTRY.CREATED', entry: newEntry});
        },
        onNewFolderButtonClick() {
            this.entryIndexService.send({type: 'SHOW_CREATE_FOLDER_MODAL'});
        },
        onRetryButtonClick() {
            this.entryIndexService.send('RETRY')
        },
        onCreateFolderModalHide(event) {
            if (this.$refs.createFolderFormRef.isSubmitting()) {
                event.preventDefault();
            }
        },
        onCreateFolderFormCompleted({folder}) {
            setTimeout(() => {
                this.entryIndexService.send({type: 'MODAL_CLOSE'});
            }, 250);
            setTimeout(() => {
                this.entryIndexService.send({type: 'FOLDER.CREATED', folder});
            }, 300);
        },
        onRenameItemClicked() {
            this.entryIndexService.send({type: 'SHOW_RENAME_FOLDER_MODAL'});
        },
        onRenameFolderModalShown() {
            this.$refs.renameFolderFormRef.focusInput();
        },
        onRenameFolderModalHide(event) {
            if (this.$refs.renameFolderFormRef.isSubmitting()) {
                event.preventDefault();
            }
        },
        onRenameFolderFormCompleted({folder}) {
            setTimeout(() => {
                this.entryIndexService.send({type: 'MODAL_CLOSE'});
            }, 250);
            setTimeout(() => {
                this.entryIndexService.send({type: 'FOLDER.RENAMED', folder});
            }, 300);
        },
        onRenameEntryModalHide(event) {
            if (this.$refs.renameEntryFormRef.isSubmitting()) {
                event.preventDefault();
            }
        },
        onRenameEntryFormCompleted({ id, meta }) {
            setTimeout(() => {
                this.entryIndexService.send({type: 'MODAL_CLOSE'});
            }, 250);
            setTimeout(() => {
                this.entryIndexService.send({
                    type: 'ENTRY.UPDATE_META',
                    id,
                    meta
                });
            }, 300);
        },
        onHotkeysLabelClick() {
            this.$refs.ninjaKeys.open();
        },
        async onBreadcrumbDrop(event, { target }) {
            if (this.currentFolder.is_root) {
                // prevent moving something to the folder its already in
                return;
            }
            const sourceType = event.dataTransfer.getData('sourceType');
            switch (sourceType) {
                case "folder": {
                    const source = event.dataTransfer.getData('folderUUID');
                    await this.handleFolderDragAndDrop({sourceType, source, target});
                    return;
                }
                case "entry": {
                    const source = event.dataTransfer.getData('entryId');
                    await this.handleEntryDragAndDrop({sourceType, source, target});
                    return;
                }
            }
        },
        async handleFolderDragAndDrop({sourceType, source, target}) {
            try {
                // prevent moving something to the folder its already in
                if (target === this.context.currentFolder.uuid) {
                    return;
                }
                this.entryIndexService.send({type: 'FOLDER.BUSY', folderUUID: source});
                this.entryIndexService.send({type: 'FOLDER.BUSY', folderUUID: target});

                await this.execMoveFolderRequest({folderUUID: source, parentUUID: target});

                this.entryIndexService.send({type: 'FOLDER.MOVED', folderUUID: source});
            } catch (e) {
                console.error(e.message, e);
                this.$toasted.global.appError({ message: 'Failed to move folder' });
            }
            this.entryIndexService.send({type: 'FOLDER.BUSY_END', folderUUID: source});
            this.entryIndexService.send({type: 'FOLDER.BUSY_END', folderUUID: target});
        },
        async handleEntryDragAndDrop({sourceType, source, target}) {
            try {
                if (typeof source === 'string') { source = parseInt(source); }
                this.entryIndexService.send({type: 'ENTRY.BUSY', id: source});
                this.entryIndexService.send({type: 'FOLDER.BUSY', folderUUID: target});

                await this.execMoveEntryRequest({entryId: source, folderUUID: target});

                this.entryIndexService.send({type: 'ENTRY.MOVED', id: source});
                this.entryIndexService.send({type: 'FOLDER.BUSY_END', folderUUID: target});
            } catch (e) {
                console.error(e.message, e);
                this.$toasted.global.appError({ message: 'Failed to move entry' });
                this.entryIndexService.send({type: 'ENTRY.IDLE', id: source});
                this.entryIndexService.send({type: 'FOLDER.BUSY_END', folderUUID: target});
            }
        },
        async onFolderDroppedOn({sourceType, source, target}) {
            switch (sourceType) {
                case "folder": {
                    this.handleFolderDragAndDrop({sourceType, source, target});
                    return;
                }
                case "entry": {
                    this.handleEntryDragAndDrop({sourceType, source, target});
                    return;
                }
            }
        },
        async execFetchFolder({folderId}) {
            const query = gql`
                query {
                    currentUser {
                        rootFolder {
                          uuid
                        }
                    }
                    folder(uuid:"${folderId}") {
                        uuid
                        title
                        is_root
                        parent {
                          uuid
                          title
                          is_root
                          parent {
                            uuid
                            title
                            is_root
                            parent {
                              uuid
                              title
                              is_root
                              parent {
                                uuid
                                title
                                is_root
                              }
                            }
                          }
                        }
                        children {
                          uuid
                          title
                        }
                        entries {
                            id
                            url
                            is_archived
                            meta {
                              type
                              value
                            }
                        }
                    }
                }`;
            const response = await this.$apollo.provider.defaultClient.query({
                query
            });
            const {data} = response;
            const {folder, currentUser} = data;
            const {rootFolder} = currentUser;
            const {title, uuid, is_root, ...restOfFolder} = folder;
            const {entries, children: folders, parent} = restOfFolder;
            const currentFolder = {title, uuid, is_root};
            return {entries, folders, currentFolder, rootFolder, parent};
        },
        async execCreateEntryRequest({url}) {
            const parentFolderUUID = this.context.folderUUID;
            const mutation = gql`
                mutation {
                  createEntry(url:"${url}",folder_uuid:"${parentFolderUUID}") {
                    id
                    url
                    meta {
                      type
                      value
                    }
                  }
                }
            `;
            const response = await this.$apollo.provider.defaultClient.mutate({
                mutation
            });
            const {data} = response;
            const {createEntry: entry} = data;
            return {entry};
        },
        async execCreateFolderRequest(name) {
            const parentFolderUUID = this.context.folderUUID;
            const mutation = gql`
                mutation {
                  createFolder(title:"${name}",parent_uuid:"${parentFolderUUID}") {
                    uuid
                    title
                  }
                }
            `;
            const response = await this.$apollo.provider.defaultClient.mutate({
                mutation
            });
            const {data} = response;
            const {createFolder: folder} = data;
            return {folder};
        },
        async execRenameFolderRequest(name) {
            const folderUUID = this.context.folderUUID;
            const mutation = gql`
                mutation {
                  renameFolder(title:"${name}",folder_uuid:"${folderUUID}") {
                    uuid
                    title
                  }
                }
            `;
            const response = await this.$apollo.provider.defaultClient.mutate({
                mutation
            });
            const {data} = response;
            const {renameFolder: folder} = data;
            return {folder};
        },
        async execRenameEntryRequest(name) {
            const entryId = this.context.editingEntry.data.id;
            const mutation = gql`
                mutation {
                  renameEntry(title:"${name}",entry_id:"${entryId}") {
                    id
                    meta {
                      type
                      value
                    }
                  }
                }
            `;

            const response = await this.$apollo.provider.defaultClient.mutate({
                mutation
            });

            const meta = response.data.renameEntry.meta;

            return {
                id: entryId,
                meta
            };
        },
        async execMoveFolderRequest({folderUUID, parentUUID}) {
            const mutation = gql`
                mutation {
                  moveFolder(folder_uuid:"${folderUUID}", parent_uuid:"${parentUUID}") {
                    uuid
                    title
                  }
                }
            `;
            const response = await this.$apollo.provider.defaultClient.mutate({
                mutation
            });
            const {data} = response;
            const {moveFolder: folder} = data;
            return {folder};
        },
        async execMoveEntryRequest({entryId, folderUUID}) {
            const mutation = gql`
                mutation {
                  moveEntry(entry_id:${entryId}, folder_uuid:"${folderUUID}") {
                    id
                  }
                }
            `;
            const response = await this.$apollo.provider.defaultClient.mutate({
                mutation
            });
            const {data} = response;
            const {moveEntry: entry} = data;
            return {entry};
        },
        async execRefreshEntryMeta(id) {
            const mutation = gql`
                mutation {
                  refreshEntryMeta(id:${id}) {
                    id
                    url
                    meta {
                      type
                      value
                    }
                  }
                }
            `;
            try {
                const response = await this.$apollo.provider.defaultClient.mutate({
                    mutation
                });
                const {data} = response;
                const {refreshEntryMeta: entry} = data;
                return entry;
            } catch (e) {
                console.log(e);
                throw e;
            }
        },
    }
}
</script>

<style scoped lang="scss">
.CreateEntryFormContainer {
    background: #f6f6fd;
}

.QrCodeOverlay-closeButton {
    background-color: transparent;
    outline: 0;
    position: fixed;
    top: 0;
    right: 0;
}

.QrCodeOverlay-codeCanvas {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.BreadcrumbsContainer {
    display: flex;
    align-items: flex-end;
}

.BreadcrumbsContainer-breadcrumbs {
    flex-grow: 1;
}

.BreadcrumbsContainer-hotkeys {
    padding: 0.4rem 0rem;
    align-items: center;
}

.HotkeysInfoLabel {}

.HotkeysInfoLabel-inner {
    display: flex;
    justify-content: flex-end;
}

.HotkeysInfoLabel-button {
    padding: 8px 6px;
    border-radius: 8px;
    background: rgba(1, 1, 1, .025);
    font-size: .75rem;
    outline: 0;
    border: solid 1px transparent;
}

.HotkeysInfoLabel-button:focus {
    border-color: $brandActive;
    box-shadow: 0 0 0 0.2rem rgb(111 89 169 / 50%);
}

.HotkeysInfoLabel-icon {
    margin-right: 1px;
    color: rgba(95, 94, 94, 0.5);
}
</style>
